import { useState } from "react";
import { Box, Typography, ToggleButton, ToggleButtonGroup, useTheme } from "@mui/material";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceDot,
  Legend,
} from "recharts";
import { PanelShell, StatGrid, StatTile, PanelUnavailable, FindingList } from "./PanelShell";

/**
 * The compass fit shown the way it is judged: a point cloud that should be a
 * circle centred on zero. Off-centre is hard iron, oval is soft iron. Numbers
 * alone never make that obvious; the shape does.
 */
const CompassPanel = ({ module }) => {
  const theme = useTheme();
  const fits = module?.panel?.compasses || [];
  const [selected, setSelected] = useState(0);
  const [plane, setPlane] = useState("xy");

  if (fits.length === 0) {
    return <PanelUnavailable reason={module?.skip_reason || "No compass samples in this log."} />;
  }

  const fit = fits[Math.min(selected, fits.length - 1)];
  const raw = (plane === "xy" ? fit.raw_xy : fit.raw_xz).map(([a, b]) => ({ a, b }));
  const corrected = (plane === "xy" ? fit.corrected_xy : fit.corrected_xz).map(([a, b]) => ({ a, b }));
  const labels = plane === "xy" ? ["X", "Y"] : ["X", "Z"];
  const coverageTone = fit.coverage_pct >= 45 ? "good" : fit.coverage_pct >= 25 ? "warn" : "bad";

  return (
    <PanelShell
      title="Compass Fit"
      subtitle={`${fit.samples.toLocaleString()} samples · ellipsoid fit against the field cloud`}
      health={module.health}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <ToggleButtonGroup size="small" exclusive value={selected} onChange={(e, v) => v != null && setSelected(v)}>
          {fits.map((f, i) => (
            <ToggleButton key={f.instance} value={i} sx={{ textTransform: "none", px: 2 }}>
              Compass {f.instance + 1}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <ToggleButtonGroup size="small" exclusive value={plane} onChange={(e, v) => v && setPlane(v)}>
          <ToggleButton value="xy" sx={{ px: 2 }}>
            XY
          </ToggleButton>
          <ToggleButton value="xz" sx={{ px: 2 }}>
            XZ
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <StatGrid>
        <StatTile
          label="Field strength"
          value={fit.mean_field_mgauss}
          unit="mG"
          tone={fit.mean_field_mgauss >= 250 && fit.mean_field_mgauss <= 650 ? "good" : "warn"}
        />
        <StatTile
          label="Orientation coverage"
          value={fit.coverage_pct}
          unit="%"
          tone={coverageTone}
          hint="Share of viewing directions this flight visited. Below 25% the fit is weakly constrained."
        />
        <StatTile label="Fitted offset X" value={fit.fitted_offsets[0]} unit="mG" />
        <StatTile label="Fitted offset Y" value={fit.fitted_offsets[1]} unit="mG" />
        <StatTile label="Fitted offset Z" value={fit.fitted_offsets[2]} unit="mG" />
        {fit.motor_r_squared != null && (
          <StatTile
            label="Current coupling"
            value={fit.motor_r_squared}
            unit="R²"
            tone={fit.motor_r_squared > 0.35 ? "warn" : "good"}
            hint="How much of the field variation battery current explains."
          />
        )}
      </StatGrid>

      <Box sx={{ height: 330 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 8, right: 12, left: -8, bottom: 4 }}>
            <CartesianGrid stroke="rgba(255,255,255,0.05)" />
            <XAxis
              type="number"
              dataKey="a"
              name={labels[0]}
              tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
              label={{ value: `${labels[0]} (mG)`, position: "insideBottomRight", offset: -2, fontSize: 10 }}
            />
            <YAxis
              type="number"
              dataKey="b"
              name={labels[1]}
              tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
              label={{ value: `${labels[1]} (mG)`, angle: -90, position: "insideLeft", fontSize: 10 }}
            />
            <ZAxis range={[6, 6]} />
            <Tooltip
              cursor={{ strokeDasharray: "3 3" }}
              contentStyle={{ background: "#171717", border: "1px solid rgba(255,255,255,0.12)", fontSize: 11 }}
              formatter={(v) => `${Number(v).toFixed(0)} mG`}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Scatter name="as logged" data={raw} fill="#E7C25B" fillOpacity={0.45} isAnimationActive={false} />
            <Scatter name="after fit" data={corrected} fill="#4ADE80" fillOpacity={0.55} isAnimationActive={false} />
            <ReferenceDot x={0} y={0} r={4} fill="#FFFFFF" stroke="none" />
          </ScatterChart>
        </ResponsiveContainer>
      </Box>

      <Typography variant="caption" sx={{ display: "block", mt: 1.5, color: theme.palette.text.secondary }}>
        A correctly calibrated compass traces a circle centred on the white dot as the aircraft turns. Gold is
        what was logged; green is the same data with the fitted offsets applied.
        {fit.coverage_pct < 25 && " Coverage is thin on this flight, so treat the fitted numbers as indicative."}
      </Typography>

      <FindingList findings={module.findings} />
    </PanelShell>
  );
};

export default CompassPanel;
